// Trades — listado con filtros (status pill tabs + búsqueda por símbolo),
// agrupados por día con sticky headers + total diario.

import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { Trade, TradingAccount } from '@bini-trading/shared-types';
import { api } from '../lib/api';
import { AppLayout } from '../components/AppLayout';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Select } from '../components/ui/Select';
import { cn } from '../lib/cn';
import { TradeFormModal } from '../components/trades/TradeFormModal';
import { TradeDetailModal } from '../components/trades/TradeDetailModal';

const fmtTime = (iso: string): string =>
  new Date(iso).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' });
const fmtDate = (iso: string): string =>
  new Date(iso + 'T12:00:00').toLocaleDateString('es-CL', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
const fmtNumber = (n: number, opts: Intl.NumberFormatOptions = {}): string =>
  new Intl.NumberFormat('es-CL', { maximumFractionDigits: 5, ...opts }).format(n);
const fmtMoney = (n: number, opts: Intl.NumberFormatOptions = {}): string =>
  new Intl.NumberFormat('es-CL', { maximumFractionDigits: 2, ...opts }).format(n);

type StatusFilter = 'all' | 'open' | 'closed' | 'pending';

const STATUS_TABS: { id: StatusFilter; label: string }[] = [
  { id: 'all', label: 'Todos' },
  { id: 'open', label: 'Abiertos' },
  { id: 'closed', label: 'Cerrados' },
  { id: 'pending', label: 'Pendientes' },
];

export default function Trades(): ReactElement {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlAccountId = searchParams.get('accountId') ?? '';
  const [showCreate, setShowCreate] = useState(false);
  const [detailTrade, setDetailTrade] = useState<Trade | null>(null);
  const [selectedAccountId, setSelectedAccountId] = useState<string>(urlAccountId);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [search, setSearch] = useState('');

  const { data: accounts, isLoading: accountsLoading } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => api<TradingAccount[]>('/accounts'),
  });

  const effectiveAccountId =
    selectedAccountId || (accounts && accounts.length > 0 ? accounts[0]!.id : '');

  useEffect(() => {
    if (effectiveAccountId && effectiveAccountId !== urlAccountId) {
      setSearchParams({ accountId: effectiveAccountId }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveAccountId]);

  const { data: trades, isLoading: tradesLoading } = useQuery({
    queryKey: ['trades', effectiveAccountId],
    queryFn: () =>
      api<Trade[]>(`/trades${effectiveAccountId ? `?accountId=${effectiveAccountId}` : ''}`),
    enabled: !!effectiveAccountId,
  });

  const accountOptions = useMemo(
    () => (accounts ?? []).map((a) => ({ value: a.id, label: `${a.name} · ${a.broker}` })),
    [accounts],
  );

  const filtered = useMemo(() => {
    if (!trades) return [];
    const s = search.trim().toUpperCase();
    return trades.filter((t) => {
      if (statusFilter === 'open' && t.status !== 'open') return false;
      if (statusFilter === 'closed' && t.status !== 'closed') return false;
      if (statusFilter === 'pending' && !t.journal?.isPending) return false;
      if (s && !t.symbol.toUpperCase().includes(s)) return false;
      return true;
    });
  }, [trades, statusFilter, search]);

  const grouped = useMemo(() => {
    const map = new Map<string, Trade[]>();
    for (const t of filtered) {
      const key = t.entryAt.slice(0, 10);
      const list = map.get(key) ?? [];
      list.push(t);
      map.set(key, list);
    }
    return [...map.entries()].sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [filtered]);

  const noAccounts = !accountsLoading && (!accounts || accounts.length === 0);

  return (
    <AppLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-8 py-8 sm:py-10">
        <header className="mb-8 flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-[11px] text-ink-subtle tracking-[0.22em] uppercase mb-2">
              Journal
            </div>
            <h1 className="font-display text-4xl tracking-tight text-ink leading-tight">
              Tu historia<span className="text-accent-500">,</span> en orden.
            </h1>
            <p className="text-ink-muted mt-2 text-sm">
              Cada operación con su contexto, screenshots y análisis.
            </p>
          </div>
          {!noAccounts && (
            <Button variant="primary" onClick={() => setShowCreate(true)} disabled={!accounts}>
              + Registrar trade
            </Button>
          )}
        </header>

        {noAccounts ? (
          <NoAccountsHint />
        ) : (
          <>
            {/* Filter bar */}
            <Card padded={false} className="p-3 mb-6 flex flex-wrap items-end gap-3">
              <div className="flex items-center gap-1 p-1 rounded-full bg-surface-panel/60 border border-surface-border">
                {STATUS_TABS.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setStatusFilter(tab.id)}
                    className={cn(
                      'px-3 h-8 rounded-full text-xs font-semibold uppercase tracking-wider transition-all',
                      statusFilter === tab.id
                        ? 'bg-surface-elevated text-ink shadow-soft'
                        : 'text-ink-muted hover:text-ink',
                    )}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              <div className="relative flex-1 min-w-[180px] max-w-xs">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-faint pointer-events-none">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8" />
                    <path d="M21 21l-4.35-4.35" />
                  </svg>
                </span>
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar símbolo…"
                  className="w-full h-10 pl-9 pr-3 rounded-full bg-surface-elevated border border-surface-border text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:border-accent-500/60"
                />
              </div>
              {(accounts?.length ?? 0) > 1 && (
                <div className="w-56">
                  <Select
                    label=""
                    options={accountOptions}
                    value={effectiveAccountId}
                    onChange={setSelectedAccountId}
                  />
                </div>
              )}
              <div className="ml-auto text-[11px] text-ink-subtle font-mono">
                {filtered.length} de {trades?.length ?? 0}
              </div>
            </Card>

            {tradesLoading ? (
              <div className="text-ink-subtle text-sm font-mono py-12 text-center">Cargando…</div>
            ) : !trades || trades.length === 0 ? (
              <EmptyTradesState onCreate={() => setShowCreate(true)} />
            ) : filtered.length === 0 ? (
              <Card className="text-center py-12">
                <p className="text-sm text-ink-subtle">
                  Sin trades que coincidan con los filtros actuales.
                </p>
              </Card>
            ) : (
              <div className="space-y-6">
                {grouped.map(([day, ts]) => {
                  const dayPnl = ts.reduce((s, t) => s + (t.pnl ?? 0), 0);
                  return (
                    <section key={day}>
                      <header className="sticky top-0 z-10 -mx-2 px-2 py-2 mb-2 flex items-baseline justify-between bg-surface-base/85 backdrop-blur-md">
                        <h2 className="text-xs font-semibold text-ink-muted uppercase tracking-[0.18em] capitalize">
                          {fmtDate(day)}
                        </h2>
                        <div className="text-xs font-mono tabular-nums">
                          <span className="text-ink-faint mr-2">{ts.length} trade{ts.length === 1 ? '' : 's'}</span>
                          <span
                            className={cn(
                              'font-bold',
                              dayPnl > 0
                                ? 'text-win-DEFAULT'
                                : dayPnl < 0
                                ? 'text-loss-DEFAULT'
                                : 'text-ink-muted',
                            )}
                          >
                            {fmtMoney(dayPnl, { signDisplay: 'always' })}
                          </span>
                        </div>
                      </header>
                      <div className="space-y-2">
                        {ts.map((t) => (
                          <TradeRow key={t.id} trade={t} onClick={() => setDetailTrade(t)} />
                        ))}
                      </div>
                    </section>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {showCreate && accounts && (
        <TradeFormModal
          open={showCreate}
          onClose={() => setShowCreate(false)}
          accounts={accounts}
          defaultAccountId={effectiveAccountId}
        />
      )}

      <TradeDetailModal
        trade={detailTrade}
        accounts={accounts ?? []}
        allTrades={trades ?? []}
        onClose={() => setDetailTrade(null)}
      />
    </AppLayout>
  );
}

function NoAccountsHint(): ReactElement {
  return (
    <Card padded={false} className="p-12 text-center border-dashed">
      <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-gold-sheen flex items-center justify-center text-stone-950 text-2xl font-bold">
        ◈
      </div>
      <h3 className="font-display text-2xl text-ink mb-2">Necesitás una cuenta primero</h3>
      <p className="text-sm text-ink-muted mb-6 max-w-md mx-auto">
        Antes de registrar trades, creá la cuenta de fondeo (o personal) donde se van a alojar.
      </p>
      <Link to="/accounts">
        <Button variant="primary">Crear cuenta</Button>
      </Link>
    </Card>
  );
}

function EmptyTradesState({ onCreate }: { onCreate: () => void }): ReactElement {
  return (
    <Card padded={false} className="p-12 text-center border-dashed">
      <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-gold-sheen flex items-center justify-center text-stone-950 text-2xl font-bold">
        ✎
      </div>
      <h3 className="font-display text-2xl text-ink mb-2">No hay trades aún</h3>
      <p className="text-sm text-ink-muted mb-6 max-w-md mx-auto">
        Registrá tu primera operación con su análisis y screenshots de TradingView.
      </p>
      <Button variant="primary" onClick={onCreate}>
        + Registrar primer trade
      </Button>
    </Card>
  );
}

function TradeRow({
  trade,
  onClick,
}: {
  trade: Trade;
  onClick: () => void;
}): ReactElement {
  const isLong = trade.side === 'long';
  const sideColor = isLong ? 'text-win-DEFAULT' : 'text-loss-DEFAULT';
  const sideBg = isLong
    ? 'bg-win-DEFAULT/10 border-win-DEFAULT/40'
    : 'bg-loss-DEFAULT/10 border-loss-DEFAULT/40';
  const pnl = trade.pnl ?? 0;
  const pnlColor =
    pnl > 0 ? 'text-win-DEFAULT' : pnl < 0 ? 'text-loss-DEFAULT' : 'text-ink-muted';
  const isOpen = trade.status === 'open';
  const isPending = trade.journal?.isPending ?? false;

  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full text-left rounded-xl border transition-all p-3.5 group flex items-center gap-3 sm:gap-4',
        isPending
          ? 'border-warn-DEFAULT/30 bg-warn-DEFAULT/5 hover:border-warn-DEFAULT/60'
          : 'border-surface-border bg-surface-elevated hover:border-surface-strong hover:shadow-soft',
      )}
    >
      <div
        className={cn(
          'shrink-0 w-11 h-11 rounded-lg border flex flex-col items-center justify-center font-bold',
          sideBg,
          sideColor,
        )}
      >
        <span className="text-lg leading-none">{isLong ? '↑' : '↓'}</span>
        <span className="text-[8px] uppercase tracking-wider mt-0.5">
          {isLong ? 'Long' : 'Short'}
        </span>
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="font-mono font-bold text-sm text-ink">{trade.symbol}</span>
          {isOpen && (
            <span className="text-[9px] font-bold uppercase tracking-wider text-info-DEFAULT bg-info-DEFAULT/10 border border-info-DEFAULT/30 px-1.5 py-0.5 rounded flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-info-DEFAULT animate-pulse" />
              Live
            </span>
          )}
          {isPending && (
            <span className="text-[9px] font-bold uppercase tracking-wider text-warn-DEFAULT bg-warn-DEFAULT/10 border border-warn-DEFAULT/30 px-1.5 py-0.5 rounded">
              ⚠ Pendiente
            </span>
          )}
          {trade.source === 'mt5' && (
            <span className="text-[9px] font-bold uppercase tracking-wider text-accent-500 bg-accent-500/10 border border-accent-500/30 px-1.5 py-0.5 rounded">
              MT5
            </span>
          )}
        </div>
        <div className="text-[11px] text-ink-subtle mt-0.5">
          <span className="font-mono">{fmtNumber(trade.quantity)}</span>
          {' · '}
          <span className="font-mono">{fmtNumber(trade.entryPrice)}</span>
          {trade.exitPrice != null && (
            <>
              {' → '}
              <span className="font-mono">{fmtNumber(trade.exitPrice)}</span>
            </>
          )}
          {' · '}
          <span className="font-mono">{fmtTime(trade.entryAt)}</span>
        </div>
      </div>

      <div className="text-right shrink-0">
        <div className={cn('font-mono text-base font-bold tabular-nums', pnlColor)}>
          {trade.pnl != null ? fmtNumber(trade.pnl, { signDisplay: 'always' }) : '—'}
        </div>
        <div className="flex items-center justify-end gap-1.5 mt-0.5">
          {trade.screenshots.length > 0 && (
            <span className="text-[10px] text-ink-subtle font-mono flex items-center gap-1">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <path d="M21 15l-5-5L5 21" />
              </svg>
              {trade.screenshots.length}
            </span>
          )}
          <StatusPill trade={trade} />
        </div>
      </div>
    </button>
  );
}

function StatusPill({ trade }: { trade: Trade }): ReactElement {
  if (trade.status === 'open') {
    return (
      <span className="text-[9px] uppercase tracking-wider font-semibold border px-1.5 py-0.5 rounded bg-warn-DEFAULT/10 border-warn-DEFAULT/30 text-warn-DEFAULT">
        ⏳ open
      </span>
    );
  }
  if (trade.status === 'cancelled') {
    return (
      <span className="text-[9px] uppercase tracking-wider font-semibold border px-1.5 py-0.5 rounded bg-surface-panel border-surface-border text-ink-subtle">
        cancelled
      </span>
    );
  }
  const label =
    trade.outcome === 'tp'
      ? '✓ TP'
      : trade.outcome === 'sl'
      ? '✕ SL'
      : trade.outcome === 'breakeven'
      ? '= BE'
      : trade.outcome === 'trailing'
      ? '↗ Trail'
      : trade.outcome === 'manual'
      ? '✋ Manual'
      : '✓ closed';
  const cls =
    trade.outcome === 'tp'
      ? 'bg-win-DEFAULT/10 border-win-DEFAULT/40 text-win-DEFAULT'
      : trade.outcome === 'sl'
      ? 'bg-loss-DEFAULT/10 border-loss-DEFAULT/40 text-loss-DEFAULT'
      : trade.outcome === 'breakeven'
      ? 'bg-surface-strong border-ink-muted text-ink'
      : trade.outcome === 'trailing'
      ? 'bg-warn-DEFAULT/10 border-warn-DEFAULT/40 text-warn-DEFAULT'
      : trade.outcome === 'manual'
      ? 'bg-info-DEFAULT/10 border-info-DEFAULT/40 text-info-DEFAULT'
      : 'bg-surface-panel border-surface-border text-ink-muted';
  return (
    <span className={cn('text-[9px] uppercase tracking-wider font-semibold border px-1.5 py-0.5 rounded', cls)}>
      {label}
    </span>
  );
}
